import * as React from 'react'
import Fuse from 'fuse.js'
import { useTranslation } from 'react-i18next'
import Popover from '../Popover/Popover'
import TaskPopoverContainer from '../TaskModal/TaskPopoverContainer'
import TaskPopoverHeader from '../TaskModal/TaskPopoverHeader'
import TaskPopoverTitle from '../TaskModal/TaskPopoverTitle'
import TaskPopoverContent from '../TaskModal/TaskPopoverContent'
import { ActiveStorageController, ArticleCollectionsController } from '../../controllers'
import { ArticleCollectionIcon, ArticleCollectionIconCategory, MimeTypes } from '../../types'
import styled from 'styled-components'
import Loader from '../Loaders/Loader'
import Button from '../Button/Button'
import { useDropzone } from 'react-dropzone'

const Container = styled(TaskPopoverContainer)`
	overflow-x: hidden;
	overflow-y: auto;
	max-height: 310px;
`

const CategorySection = styled.div`
	display: flex;
	flex-direction: column;
`

const CategorySectionTitle = styled.div`
	color: #646462;
	margin-bottom: 4px;
	font-size: 14px;
`

const Icons = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
`

const Icon = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 8px;
	padding: 10px;
	cursor: pointer;

	img {
		width: 32px;
		height: 32px;
	}

	&:hover {
		background: #f8f8f7;
	}

`

const EmptyContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	color: #646462;
	padding: 20px;
	min-height: 211px;
`

interface IProps {
	activator: JSX.Element
	helpcenterId: string
	onSubmit: (iconUrl: string) => void
}

interface IState {
	didInitialLoad: boolean
	search: string
	categories: string[]
	icons: ArticleCollectionIcon[]
}

const IconPopover = (props: IProps) => {
	const { t } = useTranslation()
	const { activator, helpcenterId, onSubmit } = props

	const [popoverActive, setPopoverActive] = React.useState(false)

	const [state, setState] = React.useState<IState>({
		didInitialLoad: false,
		search: '',
		categories: [],
		icons: []
	})

	const { didInitialLoad, search, categories, icons } = state

	const fuse = new Fuse<ArticleCollectionIcon>(icons, {
		shouldSort: false,
		threshold: 0.3,
		location: 0,
		distance: 100,
		minMatchCharLength: 1,
		keys: ['name'],
	});
	let filteredIcons = icons

	if (search?.length > 0) {
		const results = fuse.search(search)
		filteredIcons = results.map(result => result.item)
	}

	const fetchIcons = async () => {
		try {
			const { icon_categories, article_collection_icons } = await ArticleCollectionsController.getForm(helpcenterId, {})

			setState({
				...state,
				didInitialLoad: true,
				categories: icon_categories || [],
				icons: article_collection_icons || []
			})
		} catch (ex) {
			console.error(ex)
		}
	}

	React.useEffect(() => {
		if (popoverActive) fetchIcons().catch(console.error)

		if (!popoverActive) setState({ ...state, search: '' })
	}, [popoverActive])

	const onTogglePopover = (e) => {
		e.preventDefault()
		e.stopPropagation()

		setPopoverActive(!popoverActive)
	}

	const onPopoverClose = () => {
		setPopoverActive(false)
	}

	const onSearchChange = (e) => {
		setState({
			...state,
			search: e.currentTarget.value
		})
	}

	const onIconClick = (e, icon: ArticleCollectionIcon) => {
		e.preventDefault()

		onSubmit(icon.url)
		onPopoverClose()
	}

	const onDrop = async (acceptedFiles: File[]) => {
		const file = acceptedFiles[0]

		ActiveStorageController.uploadPublic(file, async (error, blob) => {
			if (error) { console.error(error) }
			if (blob) {
				const { url } = await ActiveStorageController.getBlobUrl(blob)

				onSubmit(url)
				onPopoverClose()
			}
		})
	}

	const { getRootProps, getInputProps, open } = useDropzone({
		onDrop: onDrop,
		multiple: false,
		accept: [
			MimeTypes.JPG,
			MimeTypes.JPEG,
			MimeTypes.PNG,
			MimeTypes.SVG
		],
	})

	return (
		<Popover
			active={popoverActive}
			activator={
				<div onClick={onTogglePopover}>
					{activator}
				</div>
			}
			onClose={onPopoverClose}
			placement='bottom-start'
		>
			<Container onClick={(e) => e.stopPropagation()} style={{ width: 355 }}>
				<TaskPopoverHeader>
					<TaskPopoverTitle>{t('IconPopover::Icon')}</TaskPopoverTitle>
				</TaskPopoverHeader>
				<TaskPopoverContent>
					{!didInitialLoad && <EmptyContainer>
						<Loader />
					</EmptyContainer>}
					{didInitialLoad && <>
						<div className='form-item'>
							<input
								type='text'
								value={search}
								onChange={onSearchChange}
								placeholder={t('IconPopover::Search')}
							/>
						</div>

						{filteredIcons.length === 0 && (
							<EmptyContainer>
								{t('IconPopover::No results found')}
							</EmptyContainer>
						)}

						{filteredIcons.length > 0 && <>
							<div className='form-item'>
								<CategorySectionTitle>{t('IconPopover::Upload your own icon')}</CategorySectionTitle>
								<span {...getRootProps()}>
									<Button type='default' text={t('IconPopover::Upload your own icon')} onClick={undefined} />
								</span>
								<input {...getInputProps()} style={{ display: 'none' }} />
							</div>

							{categories.map((category, index) => {
								const categoryIcons = filteredIcons.filter(icon => icon.category === category)

								if (categoryIcons?.length === 0) return null

								return (

									<CategorySection key={category}>
										<CategorySectionTitle>{t(`ArticleCollectionIconCategory::${category}`)}</CategorySectionTitle>
										<Icons>
											{categoryIcons.map((categoryIcon => {
												return (
													<Icon key={categoryIcon.id} onClick={(e) => onIconClick(e, categoryIcon)}>
														<img src={categoryIcon.url} />
													</Icon>
												)
											}))}
										</Icons>
									</CategorySection>
								)
							})}
						</>}
					</>}
				</TaskPopoverContent>
			</Container>
		</Popover>
	)
}

export default IconPopover 